import { CopyText } from '@optools/components/copy-text';
import { DataTablePagination } from '@optools/components/data-table/data-table-pagination';
import { DataTableViewOptions } from '@optools/components/data-table/data-table-view-options';
import { Button } from '@optools/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { AuditView } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatMonthDayYearHourMinuteSecond } from '@optools/utils/datetime';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { startCase } from 'es-toolkit';
import {
  Braces,
  Clock,
  Cog,
  IdCardLanyard,
  LucideProps,
  Mail,
  Server,
  Tags,
  Waypoints,
} from 'lucide-react';
import { ReactNode } from 'react';

import { formatDataDogTraceLinkUrl } from '../../utils';
import { AuditTableEventData } from './audit-table-event-data';
import { AuditTableResourceTags } from './audit-table-resource-tags';

const ColumnHeader = ({
  icon: Icon,
  title,
}: {
  title: string;
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >;
}) => {
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="size-4" /> {title}
    </span>
  );
};

const columns: ColumnDef<AuditView>[] = [
  {
    accessorFn: ({ sourceSystem }) => startCase(sourceSystem),
    accessorKey: 'sourceSystem',
    header: () => <ColumnHeader icon={Server} title="Source System" />,
    id: 'Source System',
  },
  {
    accessorKey: 'operation',
    header: () => <ColumnHeader icon={Cog} title="Operation" />,
    id: 'Operation',
  },

  {
    accessorKey: 'instigator',
    header: () => <ColumnHeader icon={IdCardLanyard} title="Instigator" />,
    id: 'Instigator',
  },
  {
    accessorKey: 'email',
    header: () => <ColumnHeader icon={Mail} title="Email" />,
    id: 'Email',
  },
  {
    accessorFn: ({ createdAt }) => formatMonthDayYearHourMinuteSecond(createdAt),
    accessorKey: 'createdAt',
    header: () => <ColumnHeader icon={Clock} title="Created At" />,
    id: 'Created At',
  },
  {
    accessorKey: 'traceId',
    cell: ({ cell }) => {
      return (
        <CopyText textToCopy={cell.row.original.traceId ?? ''}>
          <Button asChild variant="link">
            <a
              className="pl-0! text-xs"
              href={formatDataDogTraceLinkUrl(
                cell.row.original.traceId ?? '',
                cell.row.original.createdAt,
              )}
              rel="noreferrer"
              target="_blank">
              {cell.row.original.traceId}
            </a>
          </Button>
        </CopyText>
      );
    },
    header: () => <ColumnHeader icon={Waypoints} title="Trace ID" />,
    id: 'Trace ID',
  },
  {
    accessorKey: 'eventData',
    cell: ({ cell }) => {
      const { eventData, id, operation } = cell.row.original;
      return <AuditTableEventData auditId={id} eventData={eventData} operation={operation} />;
    },
    header: () => <ColumnHeader icon={Braces} title="Event Data" />,
    id: 'Event Data',
  },
  {
    accessorKey: 'resourceTags',
    cell: ({ cell }) => {
      const { id, operation, resourceTags } = cell.row.original;
      return <AuditTableResourceTags auditId={id} operation={operation} tags={resourceTags} />;
    },
    header: () => <ColumnHeader icon={Tags} title="Resource Tags" />,
    id: 'Resource Tags',
  },
];

export interface AuditTableProps {
  audits: AuditView[];
  children: ReactNode;
  onPaginationChange: (pagination: PaginationState) => void;
  pageIndex: number;
  pageSize: number;
  total: number | undefined;
}

export function AuditTable({
  audits,
  children,
  onPaginationChange,
  pageIndex,
  pageSize,
  total = 0,
}: AuditTableProps) {
  const table = useReactTable({
    columns,
    data: audits,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      onPaginationChange(newState);
    },
    rowCount: total,
    state: { pagination: { pageIndex, pageSize } },
  });

  return (
    <div className="grid h-full w-full animate-fade-in content-start space-y-4 overflow-auto p-1">
      <div className="flex flex-wrap items-center justify-between gap-6 **:text-xs">
        {children}
        <div className="ml-auto">
          <DataTableViewOptions table={table} />
        </div>
      </div>
      <div className="flex-1 overflow-hidden rounded-md border">
        <Table containerClassName="h-full relative">
          <TableHeader className="sticky top-0 border-b bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="px-4">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="p-4 text-xs whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={table.getAllColumns().length}>
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
